import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';

import { CategoryBreadcrumb } from '../components/catalog/CategoryBreadcrumb';
import { ButtonSpinner, Loader } from '../components/ui/Loader';
import { useLanguage } from '../hooks/useLanguage';
import { useGetProductQuery } from '../store/api/catalogApi';
import { usePurchaseProductMutation } from '../store/api/productsApi';
import { formatDate } from '../utils/dateFormatter';

export function ProductPage() {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const { currentLanguage } = useLanguage();
  const [purchasedValue, setPurchasedValue] = useState<string | null>(null);

  const { data, isLoading, error: productError } = useGetProductQuery(
    productId || '',
    { skip: !productId }
  );

  const [purchaseProduct, { isLoading: isPurchasing }] = usePurchaseProductMutation();

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="flex justify-center items-center py-16">
          <Loader />
        </div>
      </div>
    );
  }

  if (productError || !data || !productId) {
    return (
      <div className="min-h-screen">
        <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-danger mb-4">
            {t('catalog.product.error.title')}
          </h2>
          <p className="text-neutral-medium font-body mb-6">
            {'data' in (productError as { data?: { message?: string } })
              ? ((productError as { data?: { message?: string } })?.data?.message || t('errors.network'))
              : t('errors.network')}
          </p>
          <Link
            to="/catalog"
            className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
          >
            {t('catalog.product.error.back')}
          </Link>
        </div>
      </div>
    );
  }

  const product = data.data.product;
  const category = typeof product.categoryId === 'object' ? product.categoryId : null;
  
  const categoryName = category 
    ? (currentLanguage === 'ar' ? category.name : category.nameEn)
    : '';

  const handlePurchase = async () => {
    if (!productId) return;

    try {
      const result = await purchaseProduct({ productId }).unwrap();
      setPurchasedValue(result.data.purchase.value);
      
      // حساب الكاش باك إذا كان مفعلاً
      const cashbackAmount = category && category.isEnabledCashback
        ? result.data.purchase.price * category.cashbackPercentage
        : 0;
      
      // إظهار توست بالشراء والخصم
      if (cashbackAmount > 0) {
        toast.success(
          `${t('products.purchase.success')} - ${t('products.purchase.cashback')}: ${cashbackAmount.toFixed(2)} USD`
        );
      } else {
        toast.success(t('products.purchase.success'));
      }
    } catch (err) {
      const purchaseError = err as { data?: { message?: string }; message?: string };
      toast.error(
        purchaseError?.data?.message || purchaseError?.message || t('products.purchase.error')
      );
    }
  };

  const handleCopyValue = async () => {
    if (!purchasedValue) return;
    
    try {
      await navigator.clipboard.writeText(purchasedValue);
      toast.success(t('products.purchase.copied'));
    } catch {
      toast.error(t('products.purchase.copyError'));
    }
  };

  const isAvailable = product.status === 'available';

  return (
    <div className="min-h-screen">
      {/* Breadcrumb */}
      {category && <CategoryBreadcrumb category={category} />}

      {/* Product Header */}
      <div className="mb-8 animate-slide-up">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
          {product.name}
        </h1>
        
        {product.description && (
          <p className="text-neutral-medium text-lg font-body mb-6 whitespace-pre-line">
            {product.description}
          </p>
        )}

        {/* Status Badge */}
        <div className="mb-6">
          <span className={`inline-block px-3 py-1 rounded-full text-sm font-body ${
            product.status === 'available'
              ? 'bg-green-500/20 text-green-500'
              : 'bg-neutral-dark text-neutral-medium'
          }`}>
            {product.status === 'available' 
              ? t('catalog.product.available') 
              : t('catalog.product.sold')}
          </span>
        </div>
      </div>

      {/* Product Details Card */}
      <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-6 animate-scale-in">
        <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
          {t('catalog.product.details')}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Category */}
          {categoryName && category && (
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                {t('catalog.product.category')}
              </label>
              <div className="flex items-center gap-2">
                <Link
                  to={`/catalog/category/${category._id}`}
                  className="text-lg text-brand-accent font-heading font-semibold hover:underline"
                >
                  {categoryName}
                </Link>
              </div>
            </div>
          )}

          {/* Price */}
          {category && category.price != null && (
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                {t('catalog.product.price')}
              </label>
              <p className="text-2xl font-heading font-bold text-brand-accent">
                {category.price.toFixed(2)} USD
              </p>
            </div>
          )}

          {/* Cashback */}
          {category && category.isEnabledCashback && category.cashbackPercentage != null && (
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                {t('catalog.product.cashback')}
              </label>
              <p className="text-lg font-heading font-semibold text-brand-accent">
                {(category.cashbackPercentage * 100).toFixed(0)}%
              </p>
            </div>
          )}

          {/* Created Date */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('catalog.product.added')}
            </label>
            <p className="text-lg text-neutral-light font-body">
              {formatDate(product.createdAt, 'PP h:mm a', currentLanguage)}
            </p>
          </div>
        </div>

        {/* Note about product value */}
        {!purchasedValue && (
          <div className="mt-6 p-4 bg-neutral-dark/30 rounded-lg border border-neutral-dark">
            <p className="text-sm text-neutral-medium font-body">
              {t('catalog.product.valueNote')}
            </p>
          </div>
        )}

        {/* Purchased Value Display */}
        {purchasedValue && (
          <div className="mt-6 p-6 bg-brand-accent/10 rounded-lg border-2 border-brand-accent">
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-3">
              {t('products.purchase.valueTitle')}
            </h3>
            <div className="card-glass rounded-lg p-4 mb-4">
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <p className="text-xl font-mono font-bold text-brand-accent flex-1 text-center sm:text-left whitespace-pre-line break-words">
                  {purchasedValue}
                </p>
                <button
                  onClick={() => {
                    void handleCopyValue();
                  }}
                  className="flex-shrink-0 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 w-full sm:w-auto"
                  title={t('products.purchase.copy')}
                >
                  {t('products.purchase.copy')}
                </button>
              </div>
            </div>
            <p className="text-sm text-neutral-medium font-body">
              {t('products.purchase.valueNote')}
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        {isAvailable && !purchasedValue && (
          <button
            onClick={() => {
              void handlePurchase();
            }}
            disabled={isPurchasing}
            className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
          >
            {isPurchasing ? (
              <>
                <ButtonSpinner />
                <span>{t('common.loading')}</span>
              </>
            ) : (
              t('products.purchase.button')
            )}
          </button>
        )}
        {category && (
          <Link
            to={`/catalog/category/${category._id}`}
            className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300 text-center"
          >
            {t('catalog.product.backToCategory')}
          </Link>
        )}
        <Link
          to="/catalog"
          className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300 text-center"
        >
          {t('catalog.product.backToCatalog')}
        </Link>
      </div>
    </div>
  );
}

